/* 루미나 장식(링·방울) — 참고 사이트(microgaming.io) 실제 스크립트·배치 그대로(2026-09-30 운영자 지시 "링 문제 해결해").
   그림 = 관리자 '2. 디자인 토큰' > '장식 그림'(design_tokens.decor {ring1, ring2, bubble}) — 비우면 그 장식 없음(코드에 그림을 박지 않는다).
   참고 사이트 실측(1440·1280·1024 계산값 + 스크립트 원문 ug()/ai()):
     링(긴 글 구간 = 우리 소개 글·지금 시작하기): 구간 안 1240 컨테이너 기준 — 링2(441×505) right 0·bottom -12.5%·가로 +65%,
         링1(349×399) left -11.3%·top 32.4%. 떠다니기 = 링2 y 0→10px, 링1 y 0→-20px(5초 power1.inOut 왕복 무한).
         스크롤 = 구간이 화면 아래로 들어와(top bottom) 위로 나갈 때까지(bottom top) 링2 top → 10px, 링1 top → 300px(scrub 1초 따라잡기).
     방울: 떠다니기 y +10→-20→+10(5초씩 power1.inOut 무한) + 회전 0→(90·180·270·360 중 하나) 20초 선형 왕복 무한.
         WHAT'S HOT 띠 = 120px·right 21%·bottom 0(띠 전체 폭 기준), 스크롤(top bottom→bottom top) y → ±50px.
         신규게임 = 1240 컨테이너 기준 310px·right -15%·bottom 0 / 190px·left 0·top 25%·가로 -35%, 스크롤(top 75%→bottom 25%) 각각 y → ±100px.
         추천게임 = 96px·right 17.5%·bottom 0(구간 전체 폭 기준)·맨 위, 스크롤(top bottom→bottom top) y → ±50px.
     1024px 미만에서는 모두 숨김(참고 사이트 max-lg:hidden). 기기의 '동작 줄이기' 설정이면 움직이지 않고 제자리.
   모양·떠다니기·회전은 pages/HomePage.css 의 .lm-decor 규칙, 스크롤에 따른 움직임은 아래 useScrub(한 프레임 반복을 모든 장식이 함께 쓴다). */
import React, { useEffect, useMemo, useRef } from 'react';
import { useDesignTemplate } from '../../contexts/DesignTemplateContext';
import { resolveApiImageUrl } from '../../utils/logoUtils';

/* 관리자가 넣은 그림 주소 판정(띠 그림·장식 그림 공용) — 사이트 안 경로('/…', '//'·'/\' 제외) 또는 http(s) 이면서
   따옴표·괄호·역슬래시·공백·꺾쇠가 없을 때만 쓴다(배경 CSS url() 과 img src 에 그대로 들어가므로). 아니면 '' → 그리지 않는다. */
export const lmImageSrc = (raw) => {
  const v = typeof raw === 'string' ? raw.trim() : '';
  return /^(https?:\/\/|\/(?![/\\]))[^\s"'()\\<>]+$/i.test(v) ? resolveApiImageUrl(v) : '';
};

/* 장식 그림 설정 읽기 — 다른 디자인 토큰과 같은 파싱 규칙(문자열이면 JSON 으로 풀고, 실패하면 빈 값) */
export const useDecorImages = () => {
  const designTemplate = useDesignTemplate();
  return useMemo(() => {
    let dt = designTemplate?.design_tokens;
    if (typeof dt === 'string') {
      try { dt = JSON.parse(dt); } catch (e) { dt = null; }
    }
    const d = (dt?.decor && typeof dt.decor === 'object' && !Array.isArray(dt.decor)) ? dt.decor : {};
    return { ring1: lmImageSrc(d.ring1), ring2: lmImageSrc(d.ring2), bubble: lmImageSrc(d.bubble) };
  }, [designTemplate]);
};

/* ── 스크롤 따라 움직이기(참고 사이트 ScrollTrigger scrub: 1) ──
   구간 위치로 진행률(0~1)을 구하고, 화면의 진행률은 그 값을 1초 안에 따라잡는다(지수 접근, 1초에 약 96%).
   모든 장식이 한 프레임 반복을 함께 쓰고, 다 따라잡으면 멈춘다(스크롤·크기 변경 때 다시 돈다). */
const SCRUB_TAU_S = 0.3;
const SCRUB_SETTLE = 0.0005;
const groups = new Set();
let rafId = 0;
let lastT = 0;
const tick = (now) => {
  rafId = 0;
  const dt = lastT ? Math.min(0.1, (now - lastT) / 1000) : 1 / 60;
  lastT = now;
  const k = 1 - Math.exp(-dt / SCRUB_TAU_S);
  let moving = false;
  groups.forEach((g) => { if (g.step(k)) moving = true; });
  if (moving) rafId = requestAnimationFrame(tick);
  else lastT = 0;
};
const kick = () => { if (!rafId && groups.size) rafId = requestAnimationFrame(tick); };
const onWinChange = () => kick();
const reducedMotion = () => (window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false);

/* 진행률: 구간 위쪽이 화면 높이 startAt 지점에 닿을 때 0, 구간 아래쪽이 화면 높이 endAt 지점에 닿을 때 1
   (참고 사이트 start "top bottom" = startAt 1, end "bottom top" = endAt 0, "top 75%"·"bottom 25%" = 0.75·0.25) */
const rawProgress = (section, startAt, endAt) => {
  const r = section.getBoundingClientRect();
  const vh = window.innerHeight;
  const total = vh * (startAt - endAt) + r.height;
  if (total <= 0) return 0;
  return Math.min(1, Math.max(0, (vh * startAt - r.top) / total));
};

const useScrub = (layerRef, startAt, endAt, applyRef, enabled) => {
  useEffect(() => {
    const layer = layerRef.current;
    if (!enabled || !layer || reducedMotion()) return undefined;
    const section = layer.closest('section');
    if (!section) return undefined;
    const g = {
      p: null,
      step(k) {
        if (layer.offsetParent === null) return false;   // 숨김(1024px 미만)이면 계산하지 않는다
        const raw = rawProgress(section, startAt, endAt);
        this.p = this.p === null ? raw : this.p + (raw - this.p) * k;   // 처음에는 바로 그 자리(참고 사이트도 처음엔 즉시)
        applyRef.current(this.p);
        return Math.abs(raw - this.p) > SCRUB_SETTLE;
      },
    };
    groups.add(g);
    if (groups.size === 1) {
      window.addEventListener('scroll', onWinChange, { passive: true });
      window.addEventListener('resize', onWinChange);
    }
    kick();
    return () => {
      groups.delete(g);
      if (!groups.size) {
        window.removeEventListener('scroll', onWinChange);
        window.removeEventListener('resize', onWinChange);
      }
    };
  }, [layerRef, startAt, endAt, applyRef, enabled]);
};

/* 링 2개 — 긴 글 구간의 .container(1240) 안에 넣는다(위치 기준 = 그 컨테이너) */
export const DecorRings = ({ ring1, ring2 }) => {
  const layerRef = useRef(null);
  const r1Ref = useRef(null);
  const r2Ref = useRef(null);
  const applyRef = useRef(null);
  /* 참고 사이트: 링2(첫째) top → 10px, 링1(둘째) top → 300px. 시작 top 은 CSS 자리(링2 = 높이×112.5% - 링 높이, 링1 = 높이×32.4%)라
     그 차이만큼 세로로 옮긴다(링2 의 가로 +65% 는 CSS translate 로 따로 둔다) */
  applyRef.current = (p) => {
    const H = layerRef.current ? layerRef.current.offsetHeight : 0;
    if (r2Ref.current) {
      const start = H * 1.125 - r2Ref.current.offsetHeight;
      r2Ref.current.style.transform = `translateY(${((10 - start) * p).toFixed(1)}px)`;
    }
    if (r1Ref.current) {
      const start = H * 0.324;
      r1Ref.current.style.transform = `translateY(${((300 - start) * p).toFixed(1)}px)`;
    }
  };
  useScrub(layerRef, 1, 0, applyRef, Boolean(ring1 || ring2));
  if (!ring1 && !ring2) return null;
  return (
    <div className="lm-decor lm-decor-rings" ref={layerRef} aria-hidden="true">
      {ring2 && <div className="lm-ring lm-ring-2" ref={r2Ref}><img src={ring2} alt="" loading="lazy" draggable={false} /></div>}
      {ring1 && <div className="lm-ring lm-ring-1" ref={r1Ref}><img src={ring1} alt="" loading="lazy" draggable={false} /></div>}
    </div>
  );
};

/* 방울 — 자리별 배치(참고 사이트 구간별 값). scope: full = 구간 전체 폭 기준, contained = 구간 높이 × 1240 컨테이너 기준 */
const BUBBLE_LAYOUTS = {
  band: { scope: 'full', startAt: 1, endAt: 0, amp: 50, items: ['lm-bubble-band'] },
  featured: { scope: 'contained', startAt: 0.75, endAt: 0.25, amp: 100, items: ['lm-bubble-f1', 'lm-bubble-f2'] },
  reco: { scope: 'full', startAt: 1, endAt: 0, amp: 50, items: ['lm-bubble-reco'], top: true },
};
const SPIN_CHOICES = [90, 180, 270, 360];   // 참고 사이트 ai(): 회전 목표를 이 중 하나로(자리마다 한 번)
const pickSign = () => (Math.random() < 0.5 ? 1 : -1);   // 참고 사이트: 스크롤 이동 방향 ±(자리·방울마다 한 번)

export const DecorBubbles = ({ bubble, variant }) => {
  const layout = BUBBLE_LAYOUTS[variant];
  const layerRef = useRef(null);
  const boxRefs = useRef([]);
  const applyRef = useRef(null);
  const spin = useMemo(() => SPIN_CHOICES[Math.floor(Math.random() * SPIN_CHOICES.length)], []);
  const signs = useMemo(() => (layout ? layout.items.map(pickSign) : []), [layout]);
  applyRef.current = (p) => {
    boxRefs.current.forEach((el, i) => {
      if (el) el.style.transform = `translateY(${(signs[i] * layout.amp * p).toFixed(1)}px)`;
    });
  };
  useScrub(layerRef, layout ? layout.startAt : 1, layout ? layout.endAt : 0, applyRef, Boolean(bubble && layout));
  if (!bubble || !layout) return null;
  return (
    <div className={`lm-decor lm-decor-${layout.scope}${layout.top ? ' lm-decor-top' : ''}`} ref={layerRef} aria-hidden="true">
      {layout.items.map((cls, i) => (
        <div key={cls} className={`lm-bubble-box ${cls}`} ref={(el) => { boxRefs.current[i] = el; }}>
          <div className="lm-bubble-float">
            <img className="lm-bubble" src={bubble} alt="" loading="lazy" draggable={false} style={{ '--spin': `${spin}deg` }} />
          </div>
        </div>
      ))}
    </div>
  );
};
